import type {
  ActionCardData,
  IntervalValue,
  PerformanceData,
  PerformanceOverview,
  ProductScoreTrendData,
  ScoreType,
  TotalScoreTrend
} from './types';

import {ArrowDownOutlined, ArrowUpOutlined} from '@ant-design/icons';
import {Button, Divider, Loading} from '@ds/components';
import {PageLayout} from '@ds/layout';
import {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {
  getPerformance,
  getPerformanceOverview,
  getProductScoreTrends,
  getRequireUrgentAction,
  getTotalScoreTrend
} from 'store/slices/vendor-score/thunk';
import {getSelectedVendorFromStorage} from 'utils/Helper';

import PerformanceSection from './components/PerformanceSection/PerformanceSection';
import ProductScoreTrends from './components/ProductScoreTrends/ProductScoreTrends';
import ScoreCharts from './components/ScoreCharts/ScoreCharts';
import UrgentActionList from './components/UrgentActionList/UrgentActionList';
import {DAYS_OF_WEEK} from './constants';

const VendorScore: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const vendorId = getSelectedVendorFromStorage()?.id ?? '0';
  const [scoreInterval, setScoreInterval] = useState<IntervalValue>(7);
  const [performanceInterval, setPerformanceInterval] = useState<IntervalValue>(7);
  const {
    performanceOverview,
    performance,
    totalScoreTrend,
    requireUrgentAction,
    productScoreTrends,
    loading,
    error
  } = useAppSelector((state) => state.vendorScore);

  useEffect(() => {
    void dispatch(getPerformanceOverview({vendorId}));
    void dispatch(getRequireUrgentAction({vendorId}));
    void dispatch(getProductScoreTrends({vendorId}));
  }, [dispatch, vendorId]);

  useEffect(() => {
    void dispatch(getTotalScoreTrend({vendorId, interval: scoreInterval}));
  }, [dispatch, scoreInterval, vendorId]);

  useEffect(() => {
    void dispatch(getPerformance({vendorId, interval: performanceInterval}));
  }, [dispatch, performanceInterval, vendorId]);

  const chartData = useMemo(() => {
    const scores = (totalScoreTrend as TotalScoreTrend)?.scores;
    if (!Array.isArray(scores) || scores.length === 0) return undefined;

    return {
      labels: scores.map((item) => DAYS_OF_WEEK[new Date(item.date).getDay()]),
      dataset: scores.map((item) => item.total_score)
    };
  }, [totalScoreTrend]);

  const handleActionClick = (type: ScoreType) => {
    if (type === 'ptk') {
      navigate('/chortke/unfair-price', {state: {vendorId}});
      return;
    }

    navigate(`/chortke/show/${type === 'atp' ? 'BSS' : 'BSR'}`, {
      state: {
        vendorId,
        type: type === 'atp' ? 'assortment vendor' : 'assortment region',
        filter: 'OutOfStock'
      }
    });
  };

  const handleRetry = () => {
    dispatch(getPerformanceOverview({vendorId}));
    dispatch(getRequireUrgentAction({vendorId}));
    dispatch(getProductScoreTrends({vendorId}));
    dispatch(getTotalScoreTrend({vendorId, interval: scoreInterval}));
    dispatch(getPerformance({vendorId, interval: performanceInterval}));
  };

  const score = totalScoreTrend as TotalScoreTrend;
  const urgentMessages = Array.isArray(requireUrgentAction?.messages)
    ? (requireUrgentAction.messages as ActionCardData[])
    : [];
  const productTrends = Array.isArray(productScoreTrends)
    ? (productScoreTrends as ProductScoreTrendData[])
    : [];

  if (loading > 0 && !score.avg_score) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loading size="medium" mId="1" />
      </div>
    );
  }

  if (error && !score.avg_score) {
    return (
      <PageLayout contentClassName="items-center justify-center gap-4 px-4">
        <p className="text-center text-body-sm-emphasis text-destructive">
          دریافت اطلاعات امتیاز عملکرد با خطا مواجه شد.
        </p>
        <Button loading={loading > 0} onClick={handleRetry}>
          تلاش مجدد
        </Button>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <section className="flex flex-col gap-2 px-4 py-4">
        <h1 className="text-body-sm text-gray-07">امتیاز عملکرد شما</h1>
        <div className="flex items-center">
          <span className="text-subtitle-xl text-gray-09">{score.avg_score ?? 0}</span>
          <span className="text-body-sm text-gray-07">/۱۰۰</span>
          {score.trend && (
            <span
              className={`mr-2 flex items-center text-body-sm ${
                score.trend === 'ASC' ? 'text-success' : 'text-destructive'
              }`}
            >
              {score.trend === 'ASC' ? (
                <ArrowUpOutlined aria-hidden />
              ) : (
                <ArrowDownOutlined aria-hidden />
              )}
              {score.change_percentage}%
            </span>
          )}
        </div>
      </section>

      <ScoreCharts
        overview={performanceOverview as PerformanceOverview}
        trend={chartData}
        interval={scoreInterval}
        onIntervalChange={setScoreInterval}
      />

      {urgentMessages.length > 0 && (
        <>
          <Divider className="bg-gray-02" size={8} />
          <UrgentActionList messages={urgentMessages} onActionClick={handleActionClick} />
        </>
      )}

      {performance && Object.keys(performance).length > 0 && (
        <>
          <Divider className="bg-gray-02" size={8} />
          <PerformanceSection
            data={performance as PerformanceData}
            interval={performanceInterval}
            onIntervalChange={setPerformanceInterval}
          />
        </>
      )}

      {productTrends.length > 0 && (
        <>
          <Divider className="bg-gray-02" size={8} />
          <ProductScoreTrends data={productTrends} onActionClick={handleActionClick} />
        </>
      )}
    </PageLayout>
  );
};

export default VendorScore;
